import {
  computed,
  Directive,
  effect,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  runInInjectionContext,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormControlStatus, Validators } from '@angular/forms';
import { startWith } from 'rxjs';
import { DynamicObservable } from '../../../core/classes/dynamic-observable';
import { EReadToWriteMode } from './read-to-write.interface';

@Directive()
export abstract class ReadToWriteBase<FormControlDataType> implements OnInit {
  public readonly formControl = input.required<FormControl<FormControlDataType>>();
  public readonly label = input<string | null>(null);
  public readonly noDataPlaceholder = input<string | null | undefined>(null);
  public readonly shouldBeStuckInWriteMode = input<boolean>(true);
  public readonly isFormDataLoading = input<boolean>(false);

  protected readonly modeIfEdit: WritableSignal<EReadToWriteMode.read | EReadToWriteMode.write> = signal(
    EReadToWriteMode.read,
  );
  public readonly formControlValue$ = new DynamicObservable<FormControlDataType | null>(null);
  public readonly formControlValue = toSignal(this.formControlValue$.obs$);
  public readonly mode: Signal<EReadToWriteMode> = computed(() => {
    if (this.isFormDataLoading()) {
      return EReadToWriteMode.formDataLoading;
    }

    if (this.shouldBeStuckInWriteMode() || this.modeIfEdit() === EReadToWriteMode.write) {
      return EReadToWriteMode.write;
    }

    return this.formControlValue() ? EReadToWriteMode.read : EReadToWriteMode.noData;
  });
  private statusChanges: Signal<FormControlStatus | undefined> = computed(() => 'VALID');
  public readonly asterisksAddedLabel = computed(() => {
    this.statusChanges();
    const label = this.label();
    const formControl = this.formControl();

    if (label === null) {
      return '';
    }

    return formControl && formControl.status !== 'DISABLED' && formControl.hasValidator(Validators.required)
      ? `${label}*`
      : label;
  });

  private isClickedInside = false;

  public readonly EReadToWriteMode = EReadToWriteMode;
  private readonly injector = inject(Injector);

  constructor() {
    effect(() => {
      const formControl = this.formControl();

      if (formControl) {
        this.formControlValue$.switch(formControl.valueChanges.pipe(startWith(formControl.value)));
      }
    });
  }

  public ngOnInit(): void {
    runInInjectionContext(this.injector, () => {
      this.statusChanges = toSignal(this.formControl().statusChanges);
    });
  }

  @HostListener('mousedown', ['$event'])
  public clickInside(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).matches('.no-error-placeholder') ||
      (event.target as HTMLElement).matches('.mat-mdc-form-field-subscript-wrapper') ||
      (event.target as HTMLElement).matches('.mat-mdc-form-field-error-wrapper') ||
      (event.target as HTMLElement).matches('.mat-mdc-form-field-error') ||
      (event.target as HTMLElement).matches('.dropdown-error') ||
      (event.target as HTMLElement).matches('.custom-mat-error-wrapper')
    ) {
      this.isClickedInside = false;

      return;
    }

    this.modeIfEdit.set(EReadToWriteMode.write);
    this.isClickedInside = true;
    this.formControl().markAsTouched();
  }

  @HostListener('document:mousedown')
  public clickOut(): void {
    if (this.modeIfEdit() === EReadToWriteMode.write && !this.isClickedInside) {
      this.modeIfEdit.set(EReadToWriteMode.read);
    }

    this.isClickedInside = false;
  }
}
